// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the action chip on a received message (design/delegation-task J1,
// J2, J4–J6, H5). Mounted inside every received message row; it renders only
// on the chat's newest asking message (AssistantSuggestions.receivedChipFor).
//
//  - Desktop (J2): beside the bubble, level with the quoted sentence (found
//    with the suggestion's `quote`; the first line otherwise), kept clear of
//    the corner assistant's gutter (--konvo-corner-clearance).
//  - No room beside it (phones, narrow windows) (J1): it hangs off the
//    bubble's bottom edge like a reaction; the row grows to make room.
//  - The quoted words get a faint highlight while the chip shows (the CSS
//    Custom Highlight API, so the message's DOM is never touched).
//  - Read past (H5): once the reader replied in the chat or scrolled the
//    message away upwards, the chip shrinks to a small mark level with the
//    quote and the words get a dotted underline; hover or tap brings it back.
//    Once you press anything on the revived chip (or open its menu) it stays
//    open until you click elsewhere, so you can switch the action and act.

import {ActionChip} from '@app/features/konvo_assistant/components/action_chip/ActionChip';
import styles from '@app/features/konvo_assistant/components/action_chip/ActionChip.module.css';
import {chipLabel} from '@app/features/konvo_assistant/components/action_chip/ActionChipText';
import AssistantSuggestions from '@app/features/konvo_assistant/state/AssistantSuggestions';
import {quoteRangeIn} from '@app/features/konvo_assistant/utils/QuoteLocate';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {BellIcon, CalendarBlankIcon, CalendarPlusIcon, ClockIcon} from '@app/features/ui/icons/Icons';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

// ── Quote highlights ─────────────────────────────────────────────────────

type HighlightCtor = new (...ranges: Array<Range>) => unknown;
const SHOWING = 'konvo-action-quote';
const PAST = 'konvo-action-quote-past';
const quoteRanges = new Map<string, {range: Range; past: boolean}>();

function highlightRegistry(): {set: (name: string, value: unknown) => void; delete: (name: string) => void} | null {
	const registry = (globalThis.CSS as unknown as {highlights?: Map<string, unknown>} | undefined)?.highlights;
	return registry && typeof (globalThis as {Highlight?: unknown}).Highlight === 'function' ? registry : null;
}

let highlightStyleAdded = false;

function syncHighlights(): void {
	const registry = highlightRegistry();
	if (!registry) return;
	if (!highlightStyleAdded && typeof document !== 'undefined') {
		highlightStyleAdded = true;
		const style = document.createElement('style');
		style.dataset.konvo = 'action-chip-highlights';
		// Faint: a tint of the text colour, not a marker pen. Read past: a dotted underline only.
		style.textContent = `::highlight(${SHOWING}){background-color:color-mix(in srgb,var(--text-primary) 9%,transparent);}::highlight(${PAST}){text-decoration:underline dotted color-mix(in srgb,var(--text-primary) 55%,transparent);text-underline-offset:0.2em;}`;
		document.head.appendChild(style);
	}
	const Highlight = (globalThis as unknown as {Highlight: HighlightCtor}).Highlight;
	const all = [...quoteRanges.values()];
	registry.set(SHOWING, new Highlight(...all.filter((entry) => !entry.past).map((entry) => entry.range)));
	registry.set(PAST, new Highlight(...all.filter((entry) => entry.past).map((entry) => entry.range)));
}

function setQuoteHighlight(id: string, range: Range | null, past: boolean): void {
	if (range) quoteRanges.set(id, {range, past});
	else quoteRanges.delete(id);
	syncHighlights();
}

// ── Placement ────────────────────────────────────────────────────────────

type Mode = 'beside' | 'below' | 'mark';

interface Placement {
	mode: Mode;
	left: number;
	top: number;
	/** Beside the bubble without the quiet meta (the day and time), when only that much room is left. */
	tight: boolean;
}

const GAP = 10;
/** How far the hanging chip overlaps the bubble's bottom edge (J1): a touch, clear of the time. */
const HANG = 6;
/** Beside the bubble, the chip keeps below the hover pill that floats at the bubble's top corner. */
const BELOW_HOVER_BAR = 20;
/** In the Bubbles style the hover pill sits beside the bubble, level with its top: the chip goes under it. */
const BELOW_SIDE_BAR = 38;
/** Layers the chip opens (its menu, the forms, the event card): pressing in them keeps a revived chip open. */
const CHIP_LAYERS = '[data-popouts-root], [role="dialog"], [role="menu"]';
const MARK = 22;

function scrollParent(element: HTMLElement): HTMLElement | null {
	for (let node = element.parentElement; node; node = node.parentElement) {
		const overflow = getComputedStyle(node).overflowY;
		if (overflow === 'auto' || overflow === 'scroll') return node;
	}
	return null;
}

interface MessageActionChipProps {
	/** The store key of the chat: the Konvo channel id, or "whatsapp:<jid>". */
	chatKey: string;
	messageId: string;
	/** The bubble inside the row. */
	bubble: (row: HTMLElement) => HTMLElement | null;
	/** The element holding the message's text (defaults to the bubble). */
	text?: (row: HTMLElement) => HTMLElement | null;
	/** Whether the reader has replied in the chat since this message (read inside an observer). */
	repliedAfter: () => boolean;
}

export const MessageActionChip = observer((props: MessageActionChipProps) => {
	const received = AssistantSuggestions.receivedChipFor(props.chatKey);
	if (!received || received.suggestion.message_id !== props.messageId) return null;
	return <PlacedChip {...props} key={received.suggestion.id} />;
});

const PlacedChip = observer(({chatKey, bubble: bubbleProp, text: textProp, repliedAfter}: MessageActionChipProps) => {
	// The finders may be new functions on every render; the observers below only need the latest.
	const finders = useRef({bubble: bubbleProp, text: textProp});
	finders.current = {bubble: bubbleProp, text: textProp};
	const {i18n} = useLingui();
	const received = AssistantSuggestions.receivedChipFor(chatKey)!;
	const {chip, suggestion} = received;
	const anchorRef = useRef<HTMLSpanElement | null>(null);
	const chipRef = useRef<HTMLDivElement | null>(null);
	const [placement, setPlacement] = useState<Placement | null>(null);
	const [expanded, setExpanded] = useState(false);
	/** Pressed or its menu opened: a revived chip stays open until a press elsewhere. */
	const [pinned, setPinned] = useState(false);
	const menuOpen = useRef(false);
	/** The chip's width with its meta, and the meta's own width, from the last time it showed in full. */
	const widths = useRef<{full: number; meta: number} | null>(null);
	const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const phone = MobileLayout.enabled;
	const past = (received.readPast || repliedAfter()) && chip.phase === 'idle';
	const compact = past && !expanded && !pinned;

	const measure = useCallback(() => {
		const anchor = anchorRef.current;
		const row = anchor?.parentElement;
		if (!anchor || !row) return;
		const bubbleElement = finders.current.bubble(row);
		if (!bubbleElement) return;
		const textElement = finders.current.text?.(row) ?? bubbleElement;
		const range = quoteRangeIn(textElement, suggestion.quote);
		const rowRect = row.getBoundingClientRect();
		const bubbleRect = bubbleElement.getBoundingClientRect();
		const textRect = textElement.getBoundingClientRect();
		const lineHeight = Number.parseFloat(getComputedStyle(textElement).lineHeight) || 20;
		const line = range?.getClientRects()[0] ?? {top: textRect.top, height: lineHeight};
		const chipElement = chipRef.current;
		const chipHeight = chipElement?.offsetHeight ?? 36;
		if (chipElement && chipElement.dataset.tight !== 'true') {
			const meta = chipElement.querySelector<HTMLElement>('[data-chip-meta]');
			widths.current = {full: chipElement.offsetWidth, meta: meta ? meta.offsetWidth + 6 : 0};
		}
		const chipWidth = widths.current?.full ?? 260;
		const metaWidth = widths.current?.meta ?? 0;
		const clearance = Number.parseFloat(getComputedStyle(row).getPropertyValue('--konvo-corner-clearance')) || 0;
		// The free space runs to the chat's own edge, not the row's: WhatsApp centres a narrow
		// column of rows, with the room for J2 beside it.
		const scroller = scrollParent(row);
		const edge = Math.max(rowRect.right, scroller ? scroller.getBoundingClientRect().right - 8 : rowRect.right);
		const room = edge - clearance - 4 - (bubbleRect.right + GAP);
		let next: Placement;
		if (compact) {
			next = {
				mode: 'mark',
				left: bubbleRect.left - rowRect.left - MARK / 2,
				top: line.top - rowRect.top + (line.height - MARK) / 2,
				tight: false,
			};
		} else if (!phone && room >= chipWidth - metaWidth) {
			const top = line.top - rowRect.top + (line.height - chipHeight) / 2;
			const sideBar = row.dataset.bubble != null && row.closest('html.message-bubbles') != null;
			const min = bubbleRect.top - rowRect.top + (sideBar ? BELOW_SIDE_BAR : BELOW_HOVER_BAR);
			const max = Math.max(min, bubbleRect.bottom - rowRect.top - chipHeight);
			next = {
				mode: 'beside',
				left: bubbleRect.right - rowRect.left + GAP,
				top: Math.min(max, Math.max(min, top)),
				tight: room < chipWidth,
			};
		} else {
			const left = Math.min(bubbleRect.left - rowRect.left + 10, Math.max(0, rowRect.width - chipWidth - 8));
			next = {mode: 'below', left, top: bubbleRect.bottom - rowRect.top - HANG, tight: false};
		}
		row.dataset.konvoActionChip = next.mode;
		setPlacement((previous) =>
			previous &&
			previous.mode === next.mode &&
			previous.tight === next.tight &&
			Math.abs(previous.left - next.left) < 0.5 &&
			Math.abs(previous.top - next.top) < 0.5
				? previous
				: next,
		);
		setQuoteHighlight(suggestion.id, range, compact);
	}, [suggestion.id, suggestion.quote, compact, phone]);

	useLayoutEffect(() => {
		measure();
	});

	useEffect(() => {
		const row = anchorRef.current?.parentElement;
		if (!row) return;
		const observer = new ResizeObserver(() => measure());
		observer.observe(row);
		window.addEventListener('resize', measure);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', measure);
			delete row.dataset.konvoActionChip;
			setQuoteHighlight(suggestion.id, null, false);
		};
	}, [measure, suggestion.id]);

	// Read past: the message was on screen, then scrolled away upwards.
	useEffect(() => {
		const row = anchorRef.current?.parentElement;
		const bubbleElement = row ? finders.current.bubble(row) : null;
		if (!row || !bubbleElement || typeof IntersectionObserver === 'undefined') return;
		let seen = false;
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) seen = true;
					else if (seen && entry.rootBounds && entry.boundingClientRect.bottom <= entry.rootBounds.top) {
						AssistantSuggestions.markReadPast(suggestion.id);
					}
				}
			},
			{root: scrollParent(row)},
		);
		observer.observe(bubbleElement);
		return () => observer.disconnect();
	}, [suggestion.id]);

	useEffect(
		() => () => {
			if (collapseTimer.current) clearTimeout(collapseTimer.current);
		},
		[],
	);

	// A pinned chip folds back to its mark on a press anywhere but the chip and the layers it opened.
	useEffect(() => {
		if (!pinned) return;
		const onPointerDown = (event: PointerEvent) => {
			const target = event.target instanceof Element ? event.target : null;
			if (!target || chipRef.current?.contains(target)) return;
			if (menuOpen.current || target.closest(CHIP_LAYERS)) return;
			setPinned(false);
			setExpanded(false);
		};
		document.addEventListener('pointerdown', onPointerDown, true);
		return () => document.removeEventListener('pointerdown', onPointerDown, true);
	}, [pinned]);

	const open = () => {
		if (collapseTimer.current) clearTimeout(collapseTimer.current);
		collapseTimer.current = null;
		if (past) setExpanded(true);
	};
	const close = () => {
		if (!past || pinned || menuOpen.current) return;
		if (collapseTimer.current) clearTimeout(collapseTimer.current);
		collapseTimer.current = setTimeout(() => setExpanded(false), 600);
	};
	const pin = () => {
		open();
		if (past) setPinned(true);
	};

	const style: React.CSSProperties = placement
		? {left: placement.left, top: placement.top}
		: {left: 0, top: 0, visibility: 'hidden'};
	return (
		<>
			<span ref={anchorRef} className={styles.messageAnchor} aria-hidden="true" />
			{compact ? (
				<button
					type="button"
					className={styles.mark}
					style={{...style, width: MARK, height: MARK}}
					aria-label={chipLabel(i18n, chip)}
					title={chipLabel(i18n, chip)}
					onMouseEnter={open}
					onFocus={open}
					onClick={open}
					data-flx="konvo-assistant.action-chip.mark"
				>
					{chip.choice === 'task' || chip.choice === 'task_me' ? (
						<ClickUpLogo size={13} />
					) : chip.choice === 'reminder' ? (
						<BellIcon size={13} />
					) : chip.choice === 'event' ? (
						<CalendarPlusIcon size={13} />
					) : chip.choice === 'meeting' ? (
						<CalendarBlankIcon size={13} />
					) : (
						<ClockIcon size={13} />
					)}
				</button>
			) : (
				// biome-ignore lint/a11y/noStaticElementInteractions: hover keeps a revived chip open
				<div
					ref={chipRef}
					className={styles.onMessage}
					style={style}
					onMouseEnter={open}
					onMouseLeave={close}
					onPointerDown={pin}
					// A chip near the bottom right lifts the corner assistant above it, like the composer.
					data-corner-avoid=""
					data-placement={placement?.mode}
					data-tight={placement?.tight ? 'true' : undefined}
					data-flx="konvo-assistant.action-chip.on-message"
				>
					<ActionChip
						chip={chip}
						layout="pill"
						phone={phone}
						onDismiss={() => AssistantSuggestions.dismiss(chatKey, suggestion.id)}
						onMenu={(isOpen) => {
							menuOpen.current = isOpen;
							if (isOpen) pin();
						}}
					/>
				</div>
			)}
		</>
	);
});
